import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { TFilters } from 'redux/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import {
	downloadPartnerReportRequest,
	getPartnerReportsRequest,
	getPartnersRequest,
} from 'redux/reducers/referrals/reducer';
import {
	getDownloadingPartnerReportIds,
	getPartnerReports,
	getPartnerReportsIsLoad,
	getPartners,
} from 'redux/reducers/referrals/selectors';
import { PARTNER_REPORTS_ORDER_BY } from 'redux/reducers/referrals/constants';
import { TPartnerReport } from 'redux/reducers/referrals/types';
import { ReferralsTabs } from '../Referrals/ReferralsTabs';
import { DOWNLOAD_ACTION, tableStructure as defaultTableStructure } from './constants';
import { getPartnerReportFileName, withFilterOptions } from './utils';

const PARTNERS_OPTIONS_PAGE_SIZE = 10000;

export const ReferralsReports: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const reports = useSelector(getPartnerReports);
	const reportsLoading = useSelector(getPartnerReportsIsLoad);
	const partners = useSelector(getPartners);
	const downloadingIds = useSelector(getDownloadingPartnerReportIds);

	const tableStructure = useMemo(
		() => withFilterOptions(defaultTableStructure, partners?.data ?? []),
		[partners],
	);

	useEffect(() => {
		dispatch(getPartnersRequest({ pageNumber: 1, pageSize: PARTNERS_OPTIONS_PAGE_SIZE }));
	}, [dispatch]);

	useEffect(() => {
		const params = queryString.parse(search);
		// The default structure is enough here: the filter options do not affect the request, and
		// using it keeps the list from being requested again once the partners arrive.
		const filters = getFiltersFromNavigateParams(defaultTableStructure, params) as TFilters;

		dispatch(
			getPartnerReportsRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				filters,
				orderBy: PARTNER_REPORTS_ORDER_BY,
			}),
		);
	}, [dispatch, search]);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== DOWNLOAD_ACTION || downloadingIds.includes(id)) return;
			const report = reports?.data.find((item) => item.id === id);
			if (!report) return;

			dispatch(
				downloadPartnerReportRequest({ id, fallbackFileName: getPartnerReportFileName(report) }),
			);
		},
		[dispatch, downloadingIds, reports],
	);

	const isRowDisabled = useCallback(
		(item: TPartnerReport) => downloadingIds.includes(item.id),
		[downloadingIds],
	);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<ReferralsTabs />
			<EasTableWithFilter<TPartnerReport>
				tableStructure={tableStructure}
				items={reports}
				itemsIsLoading={reportsLoading}
				handleAction={handleAction}
				disabledRowFn={isRowDisabled}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Typography variant="body1" color="primary">
						Extra Services / Referral Hub / <b>Reports</b>
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
